import { actionSchema, type Action, type GroundSpot, type NewTask, type Spot, type TaskPatch } from './actions.js';
import { addDays, daysBetween, isDueOn, localDay } from './dates.js';
import { growthStage, isReleased, levelOf, PET_NAMES, stageRank } from './rules.js';
import {
  SHARED_BIOMES,
  type Biome, type ChecklistItem, type Context, type Daily, type DayRecord, type DayString, type GameState,
  type Habit, type InventoryItem, type LedgerEvent, type LedgerType, type Meta, type Notice, type Owed, type Pending,
  type Plant, type Profile, type SeedKind, type SeedMap, type Source, type StarterBiome, type Task, type Todo,
  type Unlock,
} from './types.js';

export class GameError extends Error {
  constructor(
    readonly code: 'notFound' | 'invalid' | 'notEnoughCoins' | 'noSeeds' | 'spotTaken' | 'noTree' | 'locked',
    message: string,
  ) {
    super(message);
    this.name = 'GameError';
  }
}

export interface Result {
  state: GameState;
  /** New ledger events to append. */
  ledger: LedgerEvent[];
  /** New day records to append (one per daily per ended day). */
  days: DayRecord[];
  notices: Notice[];
}

/** Working set for one call: the copy being changed and what it produced. */
interface Tx extends Result {
  ctx: Context;
  today: DayString;
}

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x));

function meta(ctx: Context): Meta {
  const iso = ctx.now.toISOString();
  return { id: ctx.newId(), createdAt: iso, updatedAt: iso, version: 1 };
}

export function newGame(
  opts: { timezone: string; dayStartHour?: number; starterBiome?: StarterBiome },
  ctx: Context,
): GameState {
  const profile: Profile = {
    ...meta(ctx),
    timezone: opts.timezone,
    dayStartHour: opts.dayStartHour ?? 0,
    starterBiome: opts.starterBiome ?? 'meadow',
    starterChosen: opts.starterBiome !== undefined,
    coins: 0,
    xp: 0,
    level: 1,
    pause: null,
    lastCronDay: null,
  };
  profile.lastCronDay = localDay(ctx.now, profile);
  return {
    profile,
    tasks: [],
    checklist: [],
    sections: [],
    plants: [],
    inventory: [],
    unlocks: [{ biome: profile.starterBiome, unlockedAt: ctx.now.toISOString(), reason: 'starter' }],
  };
}

function begin(state: GameState, ctx: Context): Tx {
  const s = clone(state);
  return { state: s, ledger: [], days: [], notices: [], ctx, today: localDay(ctx.now, s.profile) };
}

function finish(before: GameState, tx: Tx): Result {
  stamp(before, tx.state, tx.ctx.now);
  return { state: tx.state, ledger: tx.ledger, days: tx.days, notices: tx.notices };
}

/** Bumps `version` and `updatedAt` on every record whose content changed. */
function stamp(before: GameState, after: GameState, now: Date): void {
  const content = (r: Meta) => JSON.stringify({ ...r, createdAt: 0, updatedAt: 0, version: 0 });
  const bump = (prev: Meta | undefined, next: Meta) => {
    if (prev && content(prev) !== content(next)) {
      next.version = prev.version + 1;
      next.updatedAt = now.toISOString();
    }
  };
  bump(before.profile, after.profile);
  for (const key of ['tasks', 'checklist', 'sections', 'plants', 'inventory'] as const) {
    const old = new Map<string, Meta>((before[key] as Meta[]).map((r) => [r.id, r]));
    for (const r of after[key] as Meta[]) bump(old.get(r.id), r);
  }
}

/**
 * Applies one action. Runs the daily reset first when it's the first action
 * of a new day. Never mutates `state`.
 */
export function applyAction(state: GameState, input: unknown, ctx: Context): Result {
  const parsed = actionSchema.safeParse(input);
  if (!parsed.success) throw new GameError('invalid', parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
  const tx = begin(state, ctx);
  cron(tx);
  apply(tx, parsed.data);
  return finish(state, tx);
}

/** Runs only the daily reset. */
export function runCron(state: GameState, ctx: Context): Result {
  const tx = begin(state, ctx);
  cron(tx);
  return finish(state, tx);
}

export interface ImportTools {
  readonly state: GameState;
  /** Runs one action inside the import. */
  act(input: unknown): void;
  /** Credits growth carried over from another app, as an `import` ledger event. */
  grow(plantId: string, growth: number): void;
}

/** Runs an importer as one transaction, with the same rules as everything else. */
export function runImport(state: GameState, ctx: Context, fn: (tools: ImportTools) => void): Result {
  const tx = begin(state, ctx);
  cron(tx);
  fn({
    get state() {
      return tx.state;
    },
    act(input) {
      const parsed = actionSchema.safeParse(input);
      if (!parsed.success) throw new GameError('invalid', parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      apply(tx, parsed.data);
    },
    grow(plantId, growth) {
      const plant = findPlant(tx.state, plantId);
      record(tx, 'import', { taskId: plant.taskId, plantId, growth, source: 'import' });
    },
  });
  return finish(state, tx);
}

/* ---------- ledger, balances and unlocks ---------- */

const active = (s: GameState) => s.plants.filter((p) => !p.goneAt);

function seedItem(tx: Tx, kind: SeedKind): InventoryItem {
  let item = tx.state.inventory.find((i) => i.type === 'seed' && i.kind === kind);
  if (!item) {
    item = {
      ...meta(tx.ctx), type: 'seed', kind, quantity: 0, name: null, biome: null, x: null, y: null,
      homePlantId: null, taskId: null, day: null,
    };
    tx.state.inventory.push(item);
  }
  return item;
}

export function seedCount(s: GameState, kind: SeedKind): number {
  return s.inventory.find((i) => i.type === 'seed' && i.kind === kind)?.quantity ?? 0;
}

/** Writes a ledger event and applies its deltas to the cached balances. */
function record(
  tx: Tx,
  type: LedgerType,
  f: Partial<Pick<LedgerEvent, 'taskId' | 'plantId' | 'coins' | 'xp' | 'seeds' | 'growth' | 'reverses'>> & { source?: Source },
): LedgerEvent {
  const { ctx } = tx;
  const e: LedgerEvent = {
    id: ctx.newId(),
    type,
    taskId: f.taskId ?? null,
    plantId: f.plantId ?? null,
    occurredAt: ctx.now.toISOString(),
    localDate: tx.today,
    source: f.source ?? ctx.source,
    currency: 'coins',
    coins: f.coins ?? 0,
    xp: f.xp ?? 0,
    seeds: f.seeds ?? {},
    growth: f.growth ?? 0,
    reverses: f.reverses ?? null,
    idempotencyKey: ctx.idempotencyKey ?? null,
    rulesVersion: ctx.rules.version,
  };
  const p = tx.state.profile;
  p.coins += e.coins;
  p.xp += e.xp;
  for (const [kind, n] of Object.entries(e.seeds) as [SeedKind, number][]) seedItem(tx, kind).quantity += n;
  if (e.growth && e.plantId) {
    // A plant left behind (moved starter) may not be loaded; `reverse` never asks to shrink one.
    const plant = tx.state.plants.find((x) => x.id === e.plantId);
    if (plant) {
      plant.completions += e.growth;
      regrow(tx, plant);
    }
  }
  const level = levelOf(p.xp, ctx.rules);
  for (let l = p.level + 1; l <= level; l++) tx.notices.push({ type: 'levelUp', level: l });
  p.level = level;
  unlockEarned(tx);
  tx.ledger.push(e);
  return e;
}

/**
 * Opens every released shared biome the player has earned: by level, or the sacred grove by a
 * first mega tree. Runs on each payout and at the start of every transaction, so a biome
 * released in a later rules version opens for players who already passed its level.
 */
function unlockEarned(tx: Tx): void {
  const { rules } = tx.ctx;
  const level = tx.state.profile.level;
  for (const biome of SHARED_BIOMES) if (level >= rules.biomeUnlockLevels[biome]) unlock(tx, biome, 'level');
  if (active(tx.state).some((p) => p.kind === 'tree' && p.stage === 'mega')) unlock(tx, 'sacredGrove', 'megaTree');
}

function unlock(tx: Tx, biome: Biome, reason: Unlock['reason']): void {
  if (!isReleased(tx.ctx.rules, biome)) return;
  if (tx.state.unlocks.some((u) => u.biome === biome)) return;
  tx.state.unlocks.push({ biome, unlockedAt: tx.ctx.now.toISOString(), reason });
  tx.notices.push({ type: 'biomeUnlocked', biome, reason });
}

export const isUnlocked = (s: GameState, biome: Biome) => s.unlocks.some((u) => u.biome === biome);

/** Sets a plant's stage from its completions; the first mega tree opens the sacred grove. */
function regrow(tx: Tx, plant: Plant): void {
  const next = growthStage(plant.completions, plant.pace, tx.ctx.rules);
  if (stageRank(next) > stageRank(plant.stage)) {
    tx.notices.push({ type: 'grew', plantId: plant.id, taskId: plant.taskId, stage: next });
  }
  plant.stage = next;
  if (plant.kind === 'tree' && next === 'mega') unlock(tx, 'sacredGrove', 'megaTree');
}

/** Pays a completion: coins, XP, a chance at a seed, and one step of growth. */
function pay(tx: Tx, task: Task, type: 'complete' | 'habitUp', plant: Plant | undefined): LedgerEvent {
  const { rules } = tx.ctx;
  const r = rules.rewards[task.difficulty];
  const seeds: SeedMap = tx.ctx.random() < r.seedChance ? { [rules.seedFor[task.difficulty]]: 1 } : {};
  const e = record(tx, type, { taskId: task.id, plantId: plant?.id ?? null, coins: r.coins, xp: r.xp, seeds, growth: plant ? 1 : 0 });
  const kept = settle(tx, task, e);
  tx.notices.push({ type: 'reward', taskId: task.id, ...kept });
  return e;
}

const neg = (n: number) => (n ? -n : 0);
const isEmpty = (o: { coins: number; xp: number; seeds: SeedMap }) => !o.coins && !o.xp && !Object.keys(o.seeds).length;

/**
 * Covers what an earlier undo of this task couldn't take back, out of a payout just made. Each
 * amount covered is a `reverse` event of the original payout, so every payout's reversals add up
 * to at most what it paid, and balances never dip below zero. Returns what the player keeps.
 */
function settle(tx: Tx, task: Task, paid: LedgerEvent): { coins: number; xp: number; seeds: SeedMap } {
  const left = { coins: paid.coins, xp: paid.xp, seeds: { ...paid.seeds } };
  if (task.type === 'habit' || !task.owed?.length) return left;
  for (const o of task.owed) {
    const take = { coins: Math.min(o.coins, left.coins), xp: Math.min(o.xp, left.xp), seeds: {} as SeedMap };
    for (const [kind, n] of Object.entries(o.seeds) as [SeedKind, number][]) {
      const back = Math.min(n, left.seeds[kind] ?? 0);
      if (back <= 0) continue;
      take.seeds[kind] = back;
      if (n - back > 0) o.seeds[kind] = n - back;
      else delete o.seeds[kind];
      if ((left.seeds[kind] ?? 0) - back > 0) left.seeds[kind] = left.seeds[kind]! - back;
      else delete left.seeds[kind];
    }
    if (isEmpty(take)) continue;
    o.coins -= take.coins;
    o.xp -= take.xp;
    left.coins -= take.coins;
    left.xp -= take.xp;
    const seeds: SeedMap = {};
    for (const [kind, n] of Object.entries(take.seeds) as [SeedKind, number][]) seeds[kind] = -n;
    record(tx, 'reverse', { taskId: task.id, coins: neg(take.coins), xp: neg(take.xp), seeds, reverses: o.eventId });
  }
  task.owed = task.owed.filter((o) => !isEmpty(o));
  if (!task.owed.length) delete task.owed;
  return left;
}

const pendingOf = (e: LedgerEvent): Pending['events'][number] => ({
  id: e.id, coins: e.coins, xp: e.xp, seeds: e.seeds, growth: e.growth, plantId: e.plantId,
});

/**
 * Reverses what a completion paid, writing what was actually taken back:
 * coins, XP and seeds stop at zero (spent coins and planted seeds stay spent).
 * Whatever couldn't be taken back is kept on the task as `owed`, for its next
 * payout to cover, so completing and undoing again never pays twice.
 */
function reverse(tx: Tx, task: Daily | Todo, pending: Pending): void {
  const p = tx.state.profile;
  const total = { coins: 0, xp: 0, seeds: {} as SeedMap };
  for (const paid of [...pending.events].reverse()) {
    const coins = Math.min(p.coins, paid.coins);
    const xp = Math.min(p.xp, paid.xp);
    const seeds: SeedMap = {};
    const short: Owed = { eventId: paid.id, coins: paid.coins - coins, xp: paid.xp - xp, seeds: {} };
    for (const [kind, n] of Object.entries(paid.seeds) as [SeedKind, number][]) {
      const back = Math.min(seedCount(tx.state, kind), n);
      if (back > 0) seeds[kind] = -back;
      if (n - back > 0) short.seeds[kind] = n - back;
    }
    if (!isEmpty(short)) (task.owed ??= []).push(short);
    // Growth stays on a tree that was left behind (or isn't loaded): it is kept as it was.
    const growth = paid.plantId && active(tx.state).some((x) => x.id === paid.plantId) ? neg(paid.growth) : 0;
    record(tx, 'reverse', {
      taskId: task.id, plantId: paid.plantId, coins: neg(coins), xp: neg(xp), seeds, growth, reverses: paid.id,
    });
    total.coins += coins;
    total.xp += xp;
    for (const [kind, n] of Object.entries(seeds) as [SeedKind, number][]) total.seeds[kind] = (total.seeds[kind] ?? 0) - n;
  }
  tx.notices.push({ type: 'rewardReversed', taskId: task.id, ...total });
}

/* ---------- daily reset ---------- */

export function isPausedOn(p: Profile, day: DayString): boolean {
  return !!p.pause && p.pause.since <= day && (p.pause.until === null || day < p.pause.until);
}

const plantOf = (s: GameState, taskId: string) => s.plants.find((p) => p.taskId === taskId && p.kind !== 'weed' && !p.goneAt);

/**
 * Processes each local day that ended since the last reset, one at a time.
 * Writes a day record per daily; a due daily left undone ends its streak and
 * its leaves go sparse (its size stays); checkmarks clear; wildflowers count
 * down toward seed; weeds fade. Paused days change nothing. Running it twice
 * does nothing the second time.
 */
function cron(tx: Tx): void {
  unlockEarned(tx);
  const s = tx.state;
  const { rules } = tx.ctx;
  const last = s.profile.lastCronDay;
  if (last === null) {
    s.profile.lastCronDay = tx.today;
    return;
  }
  const elapsed = daysBetween(last, tx.today);
  if (elapsed <= 0) return;

  let wentToSeed = 0;
  for (let i = 0; i < Math.min(elapsed, 366); i++) {
    const day = addDays(last, i);
    const paused = isPausedOn(s.profile, day);
    for (const t of s.tasks) {
      if (t.type !== 'daily' || t.archivedAt) continue;
      const status = t.completed ? 'done' : paused || t.paused ? 'paused' : !isDueOn(t.repeat, day) ? 'notDue' : 'notDone';
      tx.days.push({ taskId: t.id, date: day, status });
      const plant = plantOf(s, t.id);
      if (status === 'notDone' && plant) {
        if (plant.streak > 0) tx.notices.push({ type: 'streakEnded', plantId: plant.id, streak: plant.streak });
        plant.streak = 0;
        if (plant.completions > 0) plant.leaves = 'sparse';
      }
      t.completed = false;
      t.pending = null;
      // Today's undo leftovers are let go: tomorrow's completion is new effort and pays in full.
      delete t.owed;
      for (const item of s.checklist) if (item.taskId === t.id) item.done = false;
    }
    if (paused) continue;
    for (const plant of active(s)) {
      if (plant.kind === 'wildflower' && plant.bloomed && plant.bloomDaysLeft !== null) {
        plant.bloomDaysLeft -= 1;
        if (plant.bloomDaysLeft > 0) continue;
        const seed = rules.seedFor[plant.pace];
        plant.goneAt = tx.ctx.now.toISOString();
        plant.goneReason = 'wentToSeed';
        record(tx, 'wentToSeed', { taskId: plant.taskId, plantId: plant.id, seeds: { [seed]: 1 }, source: 'system' });
        tx.notices.push({ type: 'wentToSeed', plantId: plant.id, seed });
        wentToSeed++;
      } else if (plant.kind === 'weed') {
        plant.age += 1;
        if (plant.age >= rules.weedFadeDays) {
          plant.goneAt = tx.ctx.now.toISOString();
          plant.goneReason = 'faded';
        }
      }
    }
  }
  if (s.profile.pause?.until && s.profile.pause.until <= tx.today) s.profile.pause = null;
  s.profile.lastCronDay = tx.today;
  tx.notices.push({ type: 'dayStarted', day: tx.today, daysProcessed: elapsed });
  if (elapsed > 1) tx.notices.push({ type: 'welcomeBack', daysAway: elapsed - 1, wentToSeed });
}

/* ---------- actions ---------- */

function apply(tx: Tx, a: Action): void {
  const s = tx.state;
  const now = tx.ctx.now.toISOString();
  switch (a.type) {
    case 'createTask':
      return createTask(tx, a.task);
    case 'updateTask':
      return patchTask(tx, findTask(s, a.id), a.patch);
    case 'archiveTask':
      findTask(s, a.id).archivedAt ??= now;
      return;
    case 'unarchiveTask':
      findTask(s, a.id).archivedAt = null;
      return;
    case 'moveTask':
      return moveTask(s, findTask(s, a.id), a.sectionId, a.position);
    case 'scoreTask': {
      const t = findTask(s, a.id);
      if (t.archivedAt) throw new GameError('invalid', 'That task is archived');
      if (t.type === 'habit') return scoreHabit(tx, t, a.direction);
      if (t.type === 'daily') return scoreDaily(tx, t, a.direction);
      return scoreTodo(tx, t, a.direction);
    }
    case 'addChecklistItem': {
      const t = findTask(s, a.taskId);
      if (t.type === 'habit') throw new GameError('invalid', 'Habits have no checklist');
      const position = s.checklist.filter((i) => i.taskId === t.id).length;
      s.checklist.push({ ...meta(tx.ctx), taskId: t.id, text: a.text, done: false, position });
      return;
    }
    case 'toggleChecklistItem': {
      const item = findChecklistItem(s, a.id);
      item.done = !item.done;
      return;
    }
    case 'removeChecklistItem': {
      const item = findChecklistItem(s, a.id);
      s.checklist = s.checklist.filter((i) => i !== item);
      s.checklist.filter((i) => i.taskId === item.taskId).sort(byPosition).forEach((i, n) => (i.position = n));
      return;
    }
    case 'createSection':
      s.sections.push({ ...meta(tx.ctx), name: a.name, position: s.sections.length });
      return;
    case 'renameSection':
      findSection(s, a.id).name = a.name;
      return;
    case 'deleteSection': {
      const section = findSection(s, a.id);
      s.sections = s.sections.filter((x) => x !== section);
      s.sections.sort(byPosition).forEach((x, n) => (x.position = n));
      for (const t of s.tasks) if (t.sectionId === section.id) t.sectionId = null;
      return;
    }
    case 'placePlant':
      return placePlant(s, findPlant(s, a.plantId), a.spot, tx.ctx.rules.bedPlots);
    case 'pickBloom':
      return pickBloom(tx, a.plantId, a.use);
    case 'pullWeed': {
      const weed = findPlant(s, a.plantId);
      if (weed.kind !== 'weed') throw new GameError('invalid', "That isn't a weed");
      weed.goneAt = now;
      weed.goneReason = 'pulled';
      return;
    }
    case 'plantSeed':
      return plantSeed(tx, a.seed, a.spot);
    case 'buyAnimal':
      return buyAnimal(tx, a);
    case 'renameAnimal': {
      const animal = s.inventory.find((i) => i.id === a.id && i.type === 'animal');
      if (!animal) throw new GameError('notFound', `No animal with id ${a.id}`);
      animal.name = a.name;
      return;
    }
    case 'pause':
      if (a.until !== undefined && a.until <= tx.today) throw new GameError('invalid', 'Pick a day after today to come back');
      s.profile.pause = { since: tx.today, until: a.until ?? null };
      return;
    case 'resume':
      s.profile.pause = null;
      return;
    case 'chooseStarter':
      return chooseStarter(tx, a);
    case 'editScene': {
      if (!isUnlocked(s, a.biome)) throw new GameError('locked', `${a.biome} isn't unlocked yet`);
      s.profile.scenes = { ...(s.profile.scenes ?? {}), [a.biome]: { moves: a.moves, hidden: [...new Set(a.hidden)] } };
      return;
    }
    case 'updateSettings':
      if (a.timezone !== undefined) s.profile.timezone = a.timezone;
      if (a.dayStartHour !== undefined) s.profile.dayStartHour = a.dayStartHour;
      return;
  }
}

const byPosition = (a: { position: number }, b: { position: number }) => a.position - b.position;

function newPlant(tx: Tx, kind: Plant['kind'], taskId: string | null, species: string, pace: Plant['pace']): Plant {
  return {
    ...meta(tx.ctx), taskId, kind, species, pace, biome: null, x: null, y: null, plot: null,
    stage: 'seed', completions: 0, streak: 0, leaves: 'full', bloomed: false, bloomDaysLeft: null,
    potted: false, age: 0, goneAt: null, goneReason: null,
  };
}

/** The first word of a task name, trimmed to `max` letters, with no trailing punctuation. */
export function firstWord(text: string, max: number): string {
  return (text.trim().split(/\s+/)[0] ?? '').replace(/[.,;:!?]+$/, '').slice(0, max);
}

function checkLabel(tx: Tx, label: string): string {
  if (label.length > tx.ctx.rules.labelMax) {
    throw new GameError('invalid', `A tree label can be up to ${tx.ctx.rules.labelMax} characters`);
  }
  return label;
}

function createTask(tx: Tx, n: NewTask): void {
  const s = tx.state;
  if (n.sectionId) findSection(s, n.sectionId);
  if (n.id && s.tasks.some((t) => t.id === n.id)) throw new GameError('invalid', 'A task with that id already exists');
  const difficulty = n.difficulty ?? 'easy';
  const base = {
    ...meta(tx.ctx),
    ...(n.id ? { id: n.id } : {}),
    text: n.text,
    notes: n.notes ?? '',
    difficulty,
    tags: n.tags ?? [],
    sectionId: n.sectionId ?? null,
    position: s.tasks.filter((t) => t.type === n.type).length,
    importId: null,
    archivedAt: null,
  };
  let task: Task;
  if (n.type === 'habit') {
    task = { ...base, type: 'habit', up: n.up ?? true, down: n.down ?? true, counterUp: 0, counterDown: 0 };
    s.plants.push(newPlant(tx, 'bed', task.id, `bed-${difficulty}`, difficulty));
  } else if (n.type === 'daily') {
    // The label painted on the tree: the player's, or none (tapping the tree opens its task anyway).
    const label = checkLabel(tx, n.label ?? '');
    task = {
      ...base, type: 'daily', label, repeat: n.repeat ?? { kind: 'weekly', days: [0, 1, 2, 3, 4, 5, 6] },
      paused: false, completed: false, pending: null,
    };
    s.plants.push(newPlant(tx, 'tree', task.id, tx.ctx.rules.species[difficulty], difficulty));
  } else {
    task = { ...base, type: 'todo', completed: false, completedAt: null, dueDate: n.dueDate ?? null, pending: null };
    s.plants.push(newPlant(tx, 'wildflower', task.id, `wildflower-${difficulty}`, difficulty));
  }
  s.tasks.push(task);
  if (n.type !== 'habit') {
    (n.checklist ?? []).forEach((text, position) =>
      s.checklist.push({ ...meta(tx.ctx), taskId: task.id, text, done: false, position }),
    );
  }
}

function patchTask(tx: Tx, t: Task, p: TaskPatch): void {
  const only = (field: string, type: Task['type']) => {
    if (t.type !== type) throw new GameError('invalid', `${field} only applies to a ${type}, not a ${t.type}`);
  };
  if (p.up !== undefined || p.down !== undefined) only('up/down', 'habit');
  if (p.label !== undefined || p.repeat !== undefined || p.paused !== undefined) only('label/repeat/paused', 'daily');
  if (p.dueDate !== undefined) only('dueDate', 'todo');
  if (p.text !== undefined) t.text = p.text;
  if (p.notes !== undefined) t.notes = p.notes;
  // Changes rewards from now on; the plant keeps its species and pace, so it never shrinks.
  if (p.difficulty !== undefined) t.difficulty = p.difficulty;
  if (p.tags !== undefined) t.tags = p.tags;
  if (t.type === 'habit') {
    if (p.up !== undefined) t.up = p.up;
    if (p.down !== undefined) t.down = p.down;
  } else if (t.type === 'daily') {
    if (p.label !== undefined) t.label = checkLabel(tx, p.label);
    if (p.repeat !== undefined) t.repeat = p.repeat;
    if (p.paused !== undefined) t.paused = p.paused;
  } else if (p.dueDate !== undefined) {
    t.dueDate = p.dueDate;
  }
}

function moveTask(s: GameState, t: Task, sectionId: string | null | undefined, position: number): void {
  if (sectionId) findSection(s, sectionId);
  if (sectionId !== undefined) t.sectionId = sectionId;
  const peers = s.tasks.filter((x) => x !== t && x.type === t.type && x.sectionId === t.sectionId).sort(byPosition);
  peers.splice(Math.min(position, peers.length), 0, t);
  peers.forEach((x, n) => (x.position = n));
}

function scoreHabit(tx: Tx, h: Habit, direction: 'up' | 'down'): void {
  const s = tx.state;
  const plant = plantOf(s, h.id);
  if (direction === 'up') {
    if (!h.up) throw new GameError('invalid', `"${h.text}" has no + button`);
    h.counterUp += 1;
    pay(tx, h, 'habitUp', plant);
    return;
  }
  if (!h.down) throw new GameError('invalid', `"${h.text}" has no − button`);
  h.counterDown += 1;
  record(tx, 'habitDown', { taskId: h.id, plantId: plant?.id ?? null });
  // Weeds are cosmetic: they never harm the plant, and there are only ever a few.
  const weeds = active(s).filter((p) => p.kind === 'weed' && p.taskId === h.id).length;
  if (weeds < tx.ctx.rules.maxWeedsPerHabit) {
    const weed = newPlant(tx, 'weed', h.id, 'weed', h.difficulty);
    if (plant?.plot !== null && plant?.plot !== undefined) weed.plot = plant.plot;
    s.plants.push(weed);
    tx.notices.push({ type: 'weedSprouted', plantId: weed.id, taskId: h.id });
  }
}

function scoreDaily(tx: Tx, d: Daily, direction: 'up' | 'down'): void {
  const plant = plantOf(tx.state, d.id);
  const { rules } = tx.ctx;
  if (direction === 'up') {
    if (d.completed) return;
    d.completed = true;
    if (plant) plant.streak += 1;
    const events = [pay(tx, d, 'complete', plant)];
    const streak = plant?.streak ?? 0;
    if (streak > 0 && streak % rules.weeklyBonus.every === 0) {
      const seeds: SeedMap = rules.weeklyBonus.seed ? { [rules.seedFor[d.difficulty]]: 1 } : {};
      const bonus = record(tx, 'weeklyBonus', { taskId: d.id, coins: rules.weeklyBonus.coins, seeds });
      events.push(bonus);
      // Completing again after an undo doesn't celebrate (or pay) a bonus that was already counted.
      const kept = settle(tx, d, bonus);
      if (kept.coins || Object.keys(kept.seeds).length) {
        tx.notices.push({ type: 'weeklyBonus', taskId: d.id, coins: kept.coins, seeds: kept.seeds, streak });
      }
    }
    d.pending = { events: events.map(pendingOf) };
    if (plant && plant.leaves === 'sparse' && plant.streak >= rules.leafRecovery) {
      plant.leaves = 'full';
      tx.notices.push({ type: 'leavesFull', plantId: plant.id });
    }
    return;
  }
  if (!d.completed) return;
  d.completed = false;
  if (plant) plant.streak = Math.max(0, plant.streak - 1);
  if (d.pending) reverse(tx, d, d.pending);
  d.pending = null;
}

function scoreTodo(tx: Tx, t: Todo, direction: 'up' | 'down'): void {
  const plant = plantOf(tx.state, t.id);
  if (direction === 'up') {
    if (t.completed) return;
    t.completed = true;
    t.completedAt = tx.ctx.now.toISOString();
    t.pending = { events: [pendingOf(pay(tx, t, 'complete', plant))] };
    if (plant) {
      plant.bloomed = true;
      plant.bloomDaysLeft = tx.ctx.rules.bloomDays[plant.pace];
      tx.notices.push({ type: 'bloomed', plantId: plant.id });
    }
    return;
  }
  if (!t.completed) return;
  // Undo folds the bloom back into a bud; once it's picked or gone to seed, it's kept.
  if (!plant || !plant.bloomed) {
    throw new GameError('invalid', 'That wildflower was already picked or went to seed, so it stays done');
  }
  t.completed = false;
  t.completedAt = null;
  if (t.pending) reverse(tx, t, t.pending);
  t.pending = null;
  plant.bloomed = false;
  plant.bloomDaysLeft = null;
}

function sameSpot(p: { biome: Biome | null; x: number | null; y: number | null }, spot: GroundSpot): boolean {
  return p.biome === spot.biome && p.x === spot.x && p.y === spot.y;
}

function checkGround(s: GameState, spot: GroundSpot, selfId?: string): void {
  if (!isUnlocked(s, spot.biome)) throw new GameError('locked', `${spot.biome} isn't unlocked yet`);
  const taken =
    active(s).some((p) => p.id !== selfId && p.plot === null && sameSpot(p, spot)) ||
    s.inventory.some((i) => i.x !== null && sameSpot(i, spot));
  if (taken) throw new GameError('spotTaken', 'Something is already growing there');
}

function placePlant(s: GameState, plant: Plant, spot: Spot, bedPlots: number): void {
  if (plant.kind === 'bed') {
    if (spot.where !== 'bed') throw new GameError('invalid', 'Habit plants grow in the raised bed');
    if (spot.plot >= bedPlots) throw new GameError('invalid', `The bed has plots 0–${bedPlots - 1}`);
    if (active(s).some((p) => p.kind === 'bed' && p.id !== plant.id && p.plot === spot.plot)) {
      throw new GameError('spotTaken', 'Another habit grows in that plot');
    }
    plant.plot = spot.plot;
    for (const w of active(s)) if (w.kind === 'weed' && w.taskId === plant.taskId) w.plot = spot.plot;
    return;
  }
  if (spot.where !== 'ground') throw new GameError('invalid', 'The raised bed is for habits');
  if (plant.kind === 'tree' && plant.biome !== null) throw new GameError('invalid', 'A tree is planted once and stays where it is');
  // A bud moves freely; a bloom stays put, unless it bloomed before it was ever planted.
  if (plant.kind === 'wildflower' && plant.bloomed && plant.biome !== null) throw new GameError('invalid', 'That wildflower has already bloomed');
  if (plant.kind !== 'tree' && plant.kind !== 'wildflower') throw new GameError('invalid', "That plant can't be moved");
  checkGround(s, spot, plant.id);
  plant.biome = spot.biome;
  plant.x = spot.x;
  plant.y = spot.y;
}

function pickBloom(tx: Tx, plantId: string, use: 'coins' | 'bouquet' | 'press'): void {
  const s = tx.state;
  const plant = findPlant(s, plantId);
  if (plant.kind !== 'wildflower' || !plant.bloomed) throw new GameError('invalid', "That isn't a wildflower in bloom");
  plant.goneAt = tx.ctx.now.toISOString();
  plant.goneReason = 'picked';
  plant.bloomed = false;
  plant.bloomDaysLeft = null;
  let coins = 0;
  if (use === 'coins') coins = tx.ctx.rules.pickCoins[plant.pace];
  else if (use === 'bouquet') {
    const bouquets = s.inventory.find((i) => i.type === 'decoration' && i.kind === 'bouquet');
    if (bouquets) bouquets.quantity += 1;
    else s.inventory.push(item(tx, { type: 'decoration', kind: 'bouquet' }));
  } else {
    s.inventory.push(item(tx, { type: 'pressedFlower', kind: plant.pace, taskId: plant.taskId, day: tx.today }));
  }
  record(tx, 'pick', { taskId: plant.taskId, plantId: plant.id, coins });
  tx.notices.push({ type: 'picked', plantId: plant.id, use, coins });
}

function item(tx: Tx, f: Partial<InventoryItem> & Pick<InventoryItem, 'type' | 'kind'>): InventoryItem {
  return {
    ...meta(tx.ctx), quantity: 1, name: null, biome: null, x: null, y: null, homePlantId: null, taskId: null, day: null, ...f,
  };
}

function plantSeed(tx: Tx, seed: SeedKind, spot: GroundSpot): void {
  const s = tx.state;
  if (seedCount(s, seed) <= 0) throw new GameError('noSeeds', `No ${seed} seeds left`);
  checkGround(s, spot);
  const flower = { ...newPlant(tx, 'flower', null, seed, 'easy'), biome: spot.biome, x: spot.x, y: spot.y, stage: 'medium' as const };
  s.plants.push(flower);
  record(tx, 'plantSeed', { plantId: flower.id, seeds: { [seed]: -1 } });
  tx.notices.push({ type: 'planted', plantId: flower.id, seed });
}

/** Placed trees big enough for this animal that don't already house one of its kind. */
export function treesWithRoom(s: GameState, kind: Parameters<typeof animalRule>[1], rules: Context['rules']): Plant[] {
  const min = animalRule(rules, kind).minTreeStage;
  if (min === null) return [];
  return active(s).filter(
    (p) =>
      p.kind === 'tree' &&
      p.biome !== null &&
      stageRank(p.stage) >= stageRank(min) &&
      !s.inventory.some((i) => i.type === 'animal' && i.kind === kind && i.homePlantId === p.id),
  );
}

const animalRule = (rules: Context['rules'], kind: keyof Context['rules']['animals']) => rules.animals[kind];

/** What a player leaves behind by moving the starting place: see `chooseStarter` in actions.ts. */
export function starterMoveCost(s: GameState): { trees: number; wildflowers: number; flowers: number; companions: number; touched: boolean } {
  const from = s.profile.starterBiome;
  const here = active(s).filter((p) => p.biome === from && p.plot === null);
  const trees = here.filter((p) => p.kind === 'tree');
  const companions = s.inventory.filter((i) => i.type === 'animal' && i.homePlantId && trees.some((t) => t.id === i.homePlantId)).length;
  const touched = s.plants.some((p) => !p.goneAt && p.biome !== null) || s.inventory.some((i) => i.type === 'animal');
  return {
    trees: trees.length,
    wildflowers: here.filter((p) => p.kind === 'wildflower').length,
    flowers: here.filter((p) => p.kind === 'flower').length,
    companions,
    touched,
  };
}

function chooseStarter(tx: Tx, a: Extract<Action, { type: 'chooseStarter' }>): void {
  const s = tx.state;
  if (!isReleased(tx.ctx.rules, a.biome)) throw new GameError('locked', `${a.biome} isn't out yet`);
  const was = s.profile.starterBiome;
  if (a.biome === was && s.profile.starterChosen) return;
  const cost = starterMoveCost(s);
  if (cost.touched && a.biome !== was) {
    if (!a.confirm) throw new GameError('invalid', 'Moving the starting place leaves what grows there behind; confirm to go ahead');
    const now = tx.ctx.now.toISOString();
    const goneTrees = new Set<string>();
    for (const p of active(s)) {
      if (p.biome !== was || p.plot !== null) continue;
      p.goneAt = now;
      p.goneReason = 'moved';
      if (p.kind === 'tree') goneTrees.add(p.id);
      // The task keeps going; its tree or bud starts over as an unplanted seed.
      if (p.taskId && (p.kind === 'tree' || p.kind === 'wildflower')) {
        const task = s.tasks.find((t) => t.id === p.taskId);
        if (task && !task.archivedAt && !(task.type === 'todo' && task.completed)) s.plants.push(newPlant(tx, p.kind, p.taskId, p.species, p.pace));
      }
    }
    s.inventory = s.inventory.filter((i) => !(i.type === 'animal' && i.homePlantId && goneTrees.has(i.homePlantId)));
    for (const i of s.inventory) if (i.type === 'animal' && i.biome === was) i.biome = a.biome;
  }
  s.profile.starterBiome = a.biome;
  s.profile.starterChosen = true;
  const u = s.unlocks.find((x) => x.biome === was && x.reason === 'starter');
  if (u) {
    u.biome = a.biome;
    u.unlockedAt = tx.ctx.now.toISOString();
  } else s.unlocks.push({ biome: a.biome, unlockedAt: tx.ctx.now.toISOString(), reason: 'starter' });
}

function buyAnimal(tx: Tx, a: Extract<Action, { type: 'buyAnimal' }>): void {
  const s = tx.state;
  const rule = animalRule(tx.ctx.rules, a.kind);
  const what = `A ${a.kind}`;
  if (s.profile.coins < rule.price) {
    throw new GameError('notEnoughCoins', `${what} costs ${rule.price} coins; you have ${s.profile.coins}`);
  }
  const animal = item(tx, { type: 'animal', kind: a.kind, name: a.name ?? null });
  animal.name ??= PET_NAMES[parseInt(animal.id.replace(/[^0-9a-f]/gi, '').slice(-6) || '0', 16) % PET_NAMES.length]!;
  if (rule.minTreeStage !== null) {
    if (a.biome) throw new GameError('invalid', `${what} lives in a tree; pick a tree instead of a biome`);
    const room = treesWithRoom(s, a.kind, tx.ctx.rules);
    const tree = a.homePlantId ? room.find((p) => p.id === a.homePlantId) : room[0];
    if (!tree) throw new GameError('noTree', `${what} needs a planted tree that's at least ${rule.minTreeStage} and has room`);
    animal.homePlantId = tree.id;
    animal.biome = tree.biome;
  } else {
    if (a.homePlantId) throw new GameError('invalid', `${what} lives on the ground, not in a tree`);
    const biome = a.biome ?? s.profile.starterBiome;
    if (!isUnlocked(s, biome)) throw new GameError('locked', `${biome} isn't unlocked yet`);
    animal.biome = biome;
  }
  s.inventory.push(animal);
  record(tx, 'purchase', { coins: -rule.price });
  tx.notices.push({ type: 'animalJoined', itemId: animal.id, kind: a.kind, name: animal.name!, homePlantId: animal.homePlantId });
}

/* ---------- lookups ---------- */

function findTask(s: GameState, id: string): Task {
  const t = s.tasks.find((x) => x.id === id);
  if (!t) throw new GameError('notFound', `No task with id ${id}`);
  return t;
}

function findPlant(s: GameState, id: string): Plant {
  const p = active(s).find((x) => x.id === id);
  if (!p) throw new GameError('notFound', `No plant with id ${id}`);
  return p;
}

function findSection(s: GameState, id: string) {
  const x = s.sections.find((y) => y.id === id);
  if (!x) throw new GameError('notFound', `No section with id ${id}`);
  return x;
}

function findChecklistItem(s: GameState, id: string): ChecklistItem {
  const item = s.checklist.find((i) => i.id === id);
  if (!item) throw new GameError('notFound', `No checklist item with id ${id}`);
  return item;
}
